/**
 * A document and a file cross (RFC 0357 3i, S16).
 *
 * A path has one owner: a document in the catalog, or a file in a checkout.
 * Moving between them is a real move, as in Finder. A document dropped into a
 * checkout is written there as a file and leaves the catalog; a file dropped
 * among documents becomes a document and leaves the disk. Git sees the first
 * as a new file and the second as a deletion, because that is what happened.
 *
 * The disk half of each move is the daemon's (a `save` or a `remove` on the
 * source's handler); this module decides the names and does the catalog half.
 * The order is the caller's, and it matters: a document leaves the catalog
 * only once its bytes are on disk, and a file leaves the disk only once its
 * document is in the catalog, so a failure halfway loses nothing.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import type { ProjectDoc } from '../project/types'
import {
  createDocument,
  deleteDocument,
  freeDocumentPath,
  normalizeDocumentPath,
  removeEmptyFolder,
} from './mutations'
import { projectFolders } from './reads'
import type { DocumentDoc } from './types'

/** Infer a document mime from its path extension (RFC 0263: markdown/html are
 *  the first kinds). Falls back to plain text so an unknown extension still
 *  stores. Shared by every surface that creates documents (agent tool, cli). */
export function mimeFromPath(path: string): string {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  if (ext === 'md' || ext === 'markdown') return 'text/markdown'
  if (ext === 'html' || ext === 'htm') return 'text/html'
  return 'text/plain'
}

const baseOf = (path: string): string =>
  path.split('/').filter(Boolean).at(-1) ?? ''

/**
 * The file name a document is written under. Its path's last segment, given
 * an extension from its mime when it has none: a document called `deck` of
 * markdown lands as `deck.md`, so an editor and git know what it is.
 */
export function fileNameForDocument(ref: {
  path: string
  mime: string
}): string {
  const name = baseOf(String(ref.path))
  if (!name) throw new Error('the document has no name to write it under')
  if (name.includes('.')) return name
  const mime = String(ref.mime)
  const ext =
    mime === 'text/markdown' ? '.md' : mime === 'text/html' ? '.html' : '.txt'
  return `${name}${ext}`
}

/**
 * Where inside a checkout a drop on `folder` lands, relative to the source's
 * root: the source at `apps/site` dropped on at `apps/site/docs` writes under
 * `docs/`; dropped on its own row, at the root (`''`). A folder outside the
 * source is refused, since the checkout cannot write there.
 */
export function pathInSource(
  sourceFolder: string,
  folder: string,
  name: string,
): string {
  const root = normalizeDocumentPath(sourceFolder) ?? ''
  const at = normalizeDocumentPath(folder) ?? ''
  if (!root || !name) throw new Error('a checkout folder and a name are needed')
  if (at === root) return name
  if (!at.startsWith(`${root}/`))
    throw new Error(`${at} is not inside the checkout at ${root}`)
  return `${at.slice(root.length + 1)}/${name}`
}

/**
 * The text of a document about to become a file. An app record (a poll, a
 * review) is data, not text, and has no file form; a view's build output is
 * refused for the same reason it refuses a write. Both say so.
 */
export async function documentAsFile(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<{ name: string; content: string }> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const ref = project.doc()?.documents?.[documentId]
  if (!ref) throw new Error(`${documentId} is not a document in this project`)
  if (ref.dataType)
    throw new Error(
      `${String(ref.title)} is an app record (${String(ref.dataType)}), not text; it has no file form`,
    )
  const doc = await repo.find<DocumentDoc>(documentId as AnyDocumentId)
  await (doc as { whenReady?: () => Promise<void> }).whenReady?.()
  const content = doc.doc()
  if (!content) throw new Error(`${documentId} has no content to write`)
  if (content.generated)
    throw new Error(`${String(ref.title)} is build output; it has no file form`)
  if (content.data && Object.keys(content.data).length > 0)
    throw new Error(
      `${String(ref.title)} is an app record; it has no file form`,
    )
  return {
    name: fileNameForDocument({
      path: String(ref.path),
      mime: String(ref.mime),
    }),
    content: String(content.content ?? ''),
  }
}

/**
 * The document a file becomes when dropped on `folder`: the file's name under
 * that folder, made free when the name is taken there, typed by its
 * extension, titled by its name. Pure, so the sidebar can say where a drop
 * lands before anything is written.
 */
export function documentForFile(
  filePath: string,
  folder: string,
  taken: ReadonlySet<string>,
): { path: string; title: string; mime: string } {
  const name = baseOf(filePath)
  if (!name) throw new Error(`"${filePath}" is not a file path`)
  const at = normalizeDocumentPath(folder) ?? ''
  const path = freeDocumentPath(at ? `${at}/${name}` : name, taken)
  return { path, title: name, mime: mimeFromPath(name) }
}

/**
 * Finish a document's move into a checkout: once its bytes are on disk, it
 * leaves the catalog. Separate from `documentAsFile` so the disk write sits
 * between the two, and a failed write leaves the document where it was.
 */
export async function documentLeftForFile(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<void> {
  await deleteDocument(repo, projectId, documentId)
}

/**
 * Begin a file's move among documents: a document with the file's text, at a
 * free path under `folder`. The caller then removes the file from disk, and
 * on a refusal there calls `fileNotTaken` so the catalog does not keep a copy
 * of a file that is still on disk.
 */
export async function fileAsDocument(
  repo: Repo,
  projectId: string,
  file: { path: string; content: string },
  folder: string,
): Promise<DocHandle<DocumentDoc>> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const taken = new Set(
    Object.values(project.doc()?.documents ?? {}).map(
      (r) => normalizeDocumentPath(String(r.path)) ?? String(r.path),
    ),
  )
  const target = documentForFile(file.path, folder, taken)
  return createDocument(repo, projectId, {
    ...target,
    content: file.content,
    changeMessage: `moved from ${file.path}`,
  })
}

/** Undo `fileAsDocument` when the file could not leave the disk. */
export async function fileNotTaken(
  repo: Repo,
  projectId: string,
  documentId: string,
): Promise<void> {
  await deleteDocument(repo, projectId, documentId)
}

/**
 * A folder of documents about to become files in a checkout: each one's file
 * form at its place under the folder (`plan.md`, `notes/a.md`), and the
 * explicit folder entries that go with them. All read before anything is
 * written, so one document with no file form refuses the whole folder rather
 * than leaving half of it on disk. Two documents that would be one file are
 * refused the same way.
 */
export async function folderAsFiles(
  repo: Repo,
  projectId: string,
  folder: string,
): Promise<{
  files: { documentId: string; path: string; content: string }[]
  folders: string[]
}> {
  const root = normalizeDocumentPath(folder)
  if (!root) throw new Error(`"${folder}" is not a folder path`)
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const prefix = `${root}/`
  const files: { documentId: string; path: string; content: string }[] = []
  const landing = new Map<string, string>()
  for (const [id, ref] of Object.entries(project.doc()?.documents ?? {})) {
    const at = normalizeDocumentPath(String(ref.path)) ?? String(ref.path)
    if (!at.startsWith(prefix)) continue
    const file = await documentAsFile(repo, projectId, id)
    const rest = at.slice(prefix.length)
    const dir = rest.includes('/') ? rest.slice(0, rest.lastIndexOf('/')) : ''
    const path = dir ? `${dir}/${file.name}` : file.name
    const twin = landing.get(path)
    if (twin) throw new Error(`${twin} and ${id} would both move to ${path}`)
    landing.set(path, id)
    files.push({ documentId: id, path, content: file.content })
  }
  const folders = projectFolders(project.doc())
    .map((entry) => entry.path)
    .filter((path) => path === root || path.startsWith(prefix))
  if (files.length === 0 && folders.length === 0)
    throw new Error(`no documents or explicit folders under ${root}/`)
  return { files, folders }
}

/**
 * Finish a folder's move into a checkout: once every document has left for
 * its file, the explicit folder entries go too, deepest first. A folder that
 * gained a document meanwhile is not empty and stays, holding it.
 */
export async function folderLeftForFiles(
  repo: Repo,
  projectId: string,
  folders: readonly string[],
): Promise<void> {
  for (const path of [...folders].sort((a, b) => b.length - a.length)) {
    try {
      await removeEmptyFolder(repo, projectId, path)
    } catch {
      // Not empty, or already gone: either way nothing to take back.
    }
  }
}
